import { observer } from "mobx-react";
import React, { useContext, useEffect } from "react";
import {
  Container,
  Stack,
  Title,
  Text,
  Box,
  Paper,
  Group,
  CopyButton,
  Button,
} from "@mantine/core";
import { IconCheck, IconCopy, IconLink } from "@tabler/icons-react";
import { CheckBitcoinAddressStoreContext } from "../stores/CheckBitcoinAddressStore";
import AddressInput from "./AddressInput";
import AddressResultDisplay from "./AddressResultDisplay";
import AddressLookupPanel from "./AddressLookupPanel";
import AddressExplainer from "./AddressExplainer";
import CheckAddressBackground from "./CheckAddressBackground";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../../helpers/ServerRenderedBlock";
import ToolLogo from "../../helpers/ToolLogo";

const CheckBitcoinAddressContainer: React.FC = observer(() => {
  const store = useContext(CheckBitcoinAddressStoreContext);

  useEffect(() => {
    store.hydrateFromUrl();
  }, []);

  return (
    <CheckAddressBackground>
      <Container size={700} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {hasServerRenderedBlock("ssr-addr-head") ? (
            <ServerRenderedBlock id="ssr-addr-head" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                Bitcoin Address Checker
              </Title>
              <Text c="dimmed" size="sm">
                Paste any address or invoice to identify its type, verify its
                checksum, and get detailed information
              </Text>
            </Box>
          )}

          <AddressInput />

          <AddressResultDisplay />

          <AddressLookupPanel />

          {/* Copy Link section — show after successful check */}
          {store.hasChecked && store.addressInfo && (
            <Paper
              p="md"
              radius="md"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Group justify="space-between" align="center">
                <Group gap="xs">
                  <IconLink size={18} color="teal" />
                  <Text size="sm" c="dimmed">
                    Share this result:
                  </Text>
                </Group>
                <CopyButton value={store.shareableUrl}>
                  {({ copied, copy }) => (
                    <Button
                      variant={copied ? "filled" : "outline"}
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      leftSection={
                        copied ? (
                          <IconCheck size={14} />
                        ) : (
                          <IconCopy size={14} />
                        )
                      }
                      onClick={copy}
                    >
                      {copied ? "Link Copied!" : "Copy Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
              <Text
                size="xs"
                c="dimmed"
                mt="xs"
                style={{
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  opacity: 0.7,
                }}
              >
                {store.shareableUrl}
              </Text>
            </Paper>
          )}

          {/* SEO Explainer — hide after first check */}
          {!store.hasChecked && <AddressExplainer />}

          {/* Server-rendered "what it checks" + FAQ; always mounted, since it's moved, not copied */}
          <ServerRenderedBlock id="ssr-addr-guide" />

          <RizfulPitch
            lead="Tired of checking long addresses character by character? A Lightning Address is short and readable, like yourname@rizful.com."
          />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </CheckAddressBackground>
  );
});

export default CheckBitcoinAddressContainer;
